import { Box, Stack, StackProps, Typography } from '@dtx-company/design-system/src'
import { PrismicUpsellModalContentAidaBanner } from '@app/upsell-modals/src/PrismicUpsellModalContent/components/PrismicUpsellModalContentAidaBanner'
import { PrismicUpsellModalContentCTAButton } from './components/PrismicUpsellModalContentCTAButton'
import { SliceZone } from '@prismicio/react'
import { UpsellModalBodySlice, UpsellModalPrismicFieldProps } from './UpsellModalPrismicDocument'
import { UpsellModalFeatureListSlice } from './UpsellModalFeatureListSlice'
import { UpsellModalTextSlice } from './UpsellModalTextSlice'
import { useScreenSizeBreakpoints } from '@app/common/src/hooks/useScreenSizeBreakpoints'
import {
  useUpsellModalContext,
  useUpsellModalDispatch
} from '../UpsellModal/UpsellModalContextProvider'
import { useUpsellModalPrismicDocumentSWR } from './hooks/useUpsellModalPrismicDocumentSWR'
import Image from 'next/image'
import React, { forwardRef } from 'react'

type PrismicUpsellModalContentProps = {
  onCTAClick?: React.MouseEventHandler<HTMLButtonElement>
  uid: string
}

const _TopBanner = ({
  icon,
  text
}: {
  icon: UpsellModalPrismicFieldProps['top_banner_icon']
  text: UpsellModalPrismicFieldProps['top_banner_text']
}): JSX.Element | null => {
  if (!icon && !text) {
    return null
  }
  return (
    <Stack direction="row" gap={{ xs: 4, md: 5 }} alignItems={'center'}>
      {icon?.url && (
        <Image
          src={icon.url}
          alt={''}
          height={24}
          width={24}
          data-testid="upsell_modal_top_banner_icon"
        />
      )}
      {text && <Typography variant="labelMedium">{text}</Typography>}
    </Stack>
  )
}

const bodySliceComponents: Record<UpsellModalBodySlice['slice_type'], React.ComponentType<any>> = {
  feature_list: UpsellModalFeatureListSlice,
  text: UpsellModalTextSlice
}

const defaultGap: StackProps['gap'] = { xs: 4, lg: 8 }

/**
 * Renders the content from prismic for the upsell modal
 * Uses the upsell_modal content type @see https://flowcode-ui.prismic.io/masks/upsell_modal.json
 * @param uid document id from prismic
 * @returns
 */
export const PrismicUpsellModalContent = forwardRef<HTMLDivElement, PrismicUpsellModalContentProps>(
  ({ onCTAClick, uid }: PrismicUpsellModalContentProps, ref) => {
    const { isMobile, isTablet } = useScreenSizeBreakpoints()
    const context = useUpsellModalContext()
    const dispatch = useUpsellModalDispatch()
    const { data } = useUpsellModalPrismicDocumentSWR(uid, {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnMount: true
    })
    const { data: document } = data ?? {}

    const handleCTAClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
      dispatch({ type: 'INCREMENT_STEP' })
      onCTAClick?.(e)
    }
    // An error is alreagy logged in the useUpsellModalPrismicDocumentSWR hook
    if (!document) {
      return <></>
    }

    const hasVideo = Boolean(document.aida_video_mp4_url?.url)
    return (
      <Box
        ref={ref}
        sx={theme => ({
          color: theme.palette.primary.dark,
          display: { xs: 'flex', md: 'grid' },
          flexDirection: { xs: 'column', md: 'row' },
          height: '100%',
          width: '100%',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2,minmax(0,1fr))' },
          overflow: 'hidden'
        })}
        data-testid="upsell-modal-content"
      >
        {/* This will be updated in a following ticket to render prismic data */}
        <Box
          sx={theme => ({
            backgroundColor: { md: theme.palette.surface[2] },
            display: 'flex',
            flexFlow: 'column wrap',
            flex: 1,
            padding: { xs: 0, md: theme.spacing(6), lg: theme.spacing(9) },
            overflow: 'hidden'
          })}
        >
          <Stack direction="column" gap={defaultGap} flex={1} sx={{ overflow: 'hidden' }}>
            <_TopBanner icon={document.top_banner_icon} text={document.top_banner_text} />
            {document.heading && (
              <Typography id={context.ariaLabelledById} variant="headlineMedium">
                {document.heading}
              </Typography>
            )}
            <Stack direction="column" gap={defaultGap} flex={1} sx={{ overflowY: 'auto' }}>
              {document.description && (
                <Typography id={context.ariaDescribedById} variant="labelMedium">
                  {document.description}
                </Typography>
              )}
              <Stack direction="column" gap={defaultGap} flex={1}>
                {Boolean(document.body?.length) && (
                  <SliceZone slices={document.body} components={bodySliceComponents} />
                )}
              </Stack>
            </Stack>
            <Stack direction="column" gap={{ xs: 5, lg: 6 }} sx={{ flexShrink: 0 }}>
              <Stack alignItems="center">
                <PrismicUpsellModalContentAidaBanner sliceZone={document.body1} />
              </Stack>
              {document.cta_button_label && (
                <PrismicUpsellModalContentCTAButton
                  onClick={handleCTAClick}
                  heapId={document.cta_button_heap_id}
                  size={isTablet ? 'medium' : 'large'}
                >
                  {document.cta_button_label}
                </PrismicUpsellModalContentCTAButton>
              )}
            </Stack>
          </Stack>
        </Box>
        {!isMobile && (
          <Box
            sx={{
              position: 'relative',
              height: '100%',
              flexGrow: 1,
              '& video, & img': {
                position: 'absolute',
                top: 0,
                left: 0,
                height: '101%', //fix for fractional height pixel gap on bottom of video
                width: '101%', //fix for fractional width pixel gap on right side of video
                objectFit: 'cover'
              }
            }}
          >
            {hasVideo && (
              <video
                playsInline
                loop
                disablePictureInPicture
                autoPlay
                muted
                poster={document.aida_video_poster_image?.url ?? undefined}
                aria-hidden
                data-testid="upsell-modal-video"
              >
                {document.aida_video_mp4_url?.url && (
                  <source src={document.aida_video_mp4_url?.url} type="video/mp4" />
                )}
                {document.aida_video_webm_url?.url && (
                  <source src={document.aida_video_webm_url?.url} type="video/webm" />
                )}
                {document.aida_video_poster_image?.url && (
                  <Image
                    data-testid="upsell-modal-video-fallback-image"
                    src={document.aida_video_poster_image?.url}
                    alt={''}
                    height={document.aida_video_poster_image?.dimensions?.height}
                    width={document.aida_video_poster_image?.dimensions?.width}
                  />
                )}
              </video>
            )}
          </Box>
        )}
      </Box>
    )
  }
)

PrismicUpsellModalContent.displayName = 'PrismicUpsellModalContent'
